// crewbit-admin/src/components/money/AddVendorPanel.tsx
//
// §Money.StubActionSweepCompletion Phase 3. Centered card modal for the
// add-vendor HeaderQuickActions verb. Calls create_vendor SECDEF RPC
// (Migration 291). Non-destructive accent treatment.

import { useState } from 'react';
import { UserPlus } from 'lucide-react';
import { palette, spacing, transition, typography } from '../../styles/tokens';
import { createVendor } from '../../lib/vendorBillMutations';
import { lookupErrorCopy } from '../../lib/errorMap';
import { useActiveBusinessStore } from '../../hooks/useActiveBusiness';

type PaymentTerms = 'net_15' | 'net_30' | 'net_60' | 'on_receipt';

const TERM_OPTIONS: { value: PaymentTerms; label: string }[] = [
  { value: 'on_receipt', label: 'On receipt' },
  { value: 'net_15', label: 'Net 15' },
  { value: 'net_30', label: 'Net 30' },
  { value: 'net_60', label: 'Net 60' },
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onResult: (ok: boolean, message: string) => void;
  /** Optional callback fired with the new vendor_id on success. */
  onSuccess?: (vendorId: string) => void;
}

export function AddVendorPanel({ isOpen, onClose, onResult, onSuccess }: Props) {
  const businessId = useActiveBusinessStore((s) => s.activeBusinessId);

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [paymentTerms, setPaymentTerms] = useState<PaymentTerms>('net_30');
  const [serviceType, setServiceType] = useState('');
  const [loading, setLoading] = useState(false);

  if (!isOpen) return null;

  const valid = companyName.trim().length > 0;

  const reset = () => {
    setCompanyName('');
    setContactName('');
    setEmail('');
    setPhone('');
    setPaymentTerms('net_30');
    setServiceType('');
  };

  const handleClose = () => {
    if (loading) return;
    reset();
    onClose();
  };

  const onConfirm = async () => {
    if (!valid || !businessId) return;
    setLoading(true);
    const result = await createVendor({
      businessId,
      companyName: companyName.trim(),
      contactName: contactName.trim() || null,
      email: email.trim() || null,
      phone: phone.trim() || null,
      paymentTerms,
      serviceType: serviceType.trim() || null,
    });
    setLoading(false);
    if (result.success) {
      onResult(true, result.data.idempotent_replay
        ? `${companyName.trim()} already exists.`
        : `Vendor ${companyName.trim()} added.`);
      onSuccess?.(result.data.vendor_id);
      reset();
      onClose();
    } else {
      onResult(false, lookupErrorCopy('create_vendor', result.error.code));
    }
  };

  return (
    <>
      <div onClick={handleClose} style={{
        position: 'fixed', inset: 0, zIndex: 70,
        background: 'rgba(0,0,0,0.3)',
        backdropFilter: 'blur(4px)', WebkitBackdropFilter: 'blur(4px)',
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '100%', maxWidth: 460, zIndex: 71,
        background: palette.white, borderRadius: 16,
        boxShadow: '0 16px 64px rgba(0,0,0,0.14), 0 4px 16px rgba(0,0,0,0.08)',
        padding: 28,
      }}>
        <div style={{
          width: 44, height: 44, borderRadius: 14,
          background: palette.accentWash,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          marginBottom: spacing.md,
        }}>
          <UserPlus size={22} color={palette.accent} strokeWidth={1.8} />
        </div>

        <div style={{ fontSize: 16, fontWeight: 700, color: palette.g800, marginBottom: 6 }}>
          Add vendor
        </div>
        <div style={{ fontSize: 14, color: palette.g500, lineHeight: 1.5, marginBottom: spacing.md }}>
          Create a vendor record. You can attach bills to it afterward.
        </div>

        <div style={{ display: 'grid', gap: spacing.sm }}>
          <Field label="Company name" required>
            <input
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value.slice(0, 200))}
              placeholder="e.g. Home Depot"
              style={inputStyle}
              autoFocus
            />
          </Field>

          <Field label="Contact name">
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value.slice(0, 200))}
              placeholder="Optional"
              style={inputStyle}
            />
          </Field>

          <Field label="Email">
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value.slice(0, 200))}
              placeholder="Optional"
              style={inputStyle}
            />
          </Field>

          <Field label="Phone">
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.slice(0, 50))}
              placeholder="Optional"
              style={inputStyle}
            />
          </Field>

          <Field label="Service type">
            <input
              type="text"
              value={serviceType}
              onChange={(e) => setServiceType(e.target.value.slice(0, 100))}
              placeholder="e.g. Materials supplier"
              style={inputStyle}
            />
          </Field>

          <Field label="Payment terms">
            <select
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value as PaymentTerms)}
              style={inputStyle}
            >
              {TERM_OPTIONS.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </Field>
        </div>

        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: spacing.md }}>
          <button onClick={handleClose} disabled={loading} style={{
            padding: '9px 22px', borderRadius: 20,
            border: `1px solid ${palette.g200}`, background: palette.white,
            fontSize: 13.5, fontWeight: 500, color: palette.g600,
            cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            transition: `background ${transition.fast}`,
          }}>Cancel</button>
          <button onClick={onConfirm} disabled={loading || !valid} style={{
            padding: '9px 22px', borderRadius: 20, border: 'none',
            background: (loading || !valid) ? palette.g300 : palette.accent,
            color: palette.white, fontSize: 13.5, fontWeight: 600,
            cursor: (loading || !valid) ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
          }}>{loading ? 'Adding…' : 'Add vendor'}</button>
        </div>
      </div>
    </>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: spacing.sm, borderRadius: 8,
  border: `1px solid ${palette.g200}`, fontFamily: 'inherit',
  fontSize: typography.size.caption, color: palette.g800,
  boxSizing: 'border-box', background: palette.white,
};

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label style={{ display: 'block' }}>
      <div style={{ fontSize: typography.size.caption, color: palette.g700, marginBottom: spacing.xs }}>
        {label}{required && <span style={{ color: palette.red, marginLeft: 2 }}>*</span>}
      </div>
      {children}
    </label>
  );
}
